import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { api } from "../../../services/apiService";
import { trackPaywallEntry } from "../../../utils/tracking";

const MAX_QUERY = 400;
const MIN_QUERY = 3;
const REASON_DAILY = "DAILY_LIMIT_REACHED";
const REASON_BURST = "RATE_LIMIT_REACHED";

// Provenance marker stored on the quiz (Quiz.source) so power-search-built
// quizzes are distinguishable in history/analytics. A claim, not a key — the
// server validates the ids themselves, not this string.
export const POWER_SEARCH_QUIZ_SOURCE = "POWER_SEARCH";

/*
 * Qualitative relevance label for a result (A8). The backend returns a raw
 * cosine `similarity` float (e.g. 0.87); surfacing that decimal to a med
 * student is jargon noise. Bucket it into a plain-language strength label
 * instead. Exported + pure so the thresholds are unit-testable. Non-numeric
 * input yields "" so the caller can omit the chip entirely (mirrors the old
 * `typeof r.similarity === "number"` guard).
 */
export const relevanceLabel = (similarity) => {
  if (typeof similarity !== "number" || Number.isNaN(similarity)) return "";
  if (similarity >= 0.5) return "Strong match";
  if (similarity >= 0.35) return "Good match";
  return "Related";
};

// Names the quiz after the query so it is recognizable in quiz history.
// Truncates by code point (Array.from), not code unit, so a 60-char cut can
// never split a surrogate pair and leave a lone surrogate before the ellipsis.
export const powerSearchQuizName = (query) => {
  const q = String(query || "").replace(/\s+/g, " ").trim();
  const points = Array.from(q);
  const title = points.length > 60 ? `${points.slice(0, 60).join("")}…` : q;
  return title ? `Power Search: ${title}` : "Power Search quiz";
};

/*
 * Params for building a quiz from a Power Search result set, funneled through
 * the SAME createQuiz action as the form (backend: powerSearchQuestionIds on
 * POST /api/v2/quiz, server-revalidated, flag-gated). Pure so the contract is
 * unit-testable. Deliberate divergences from the form defaults:
 *  - questionOptions all-false: the user picked these exact questions; the
 *    form's `unused: true` default would silently EXCLUDE any they already
 *    attempted.
 *  - thresholdEnabled false: a High-Yield q-score filter would re-filter the
 *    explicit picks server-side and drop results below the floor.
 *  - noOfQuestions = ids.length: practice the whole result set (the server
 *    clamps down if a result went stale between search and click).
 */
export const buildPowerSearchQuizParams = ({
  userId,
  questionIds,
  query,
  examTypes,
  isTutor,
  tutorMode,
  timerMode,
}) => ({
  name: powerSearchQuizName(query),
  userId,
  source: POWER_SEARCH_QUIZ_SOURCE,
  quizOptions: {
    // Same rule as onCreateQuiz: tutor-mode only applies to tutor-role users.
    tutorOn: isTutor ? tutorMode : false,
    timerOn: timerMode,
    thresholdEnabled: false,
  },
  noOfQuestions: questionIds.length,
  questionOptions: {
    all: false,
    unused: false,
    correct: false,
    incorrect: false,
    marked: false,
  },
  // The exam scope the search RAN with (snapshotted by the panel, not the
  // click-time form selection); omitted (like the search payload) when no
  // exam was selected — the server treats absent as "no filter".
  ...(examTypes && examTypes.length ? { categoryMap: { examType: [...examTypes] } } : {}),
  powerSearchQuestionIds: [...questionIds],
});

// One-tap example queries so a first-time user isn't staring at a blank box
// wondering what to type — each is a plain-language ABSITE scenario that shows
// off "describe it, don't keyword it".
const EXAMPLE_QUERIES = [
  "bleeding duodenal ulcer",
  "workup of a thyroid nodule",
  "postoperative fever",
];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  header: { display: "flex", alignItems: "center", gap: 8 },
  beta: { fontSize: 11, fontWeight: 700, letterSpacing: 0.4 },
  sub: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1.5) },
  examples: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: theme.spacing(1.5) },
  examplesLabel: { fontSize: 12, color: theme.palette.text.secondary, marginRight: 2 },
  exampleChip: { cursor: "pointer" },
  row: { display: "flex", gap: 8, alignItems: "flex-start" },
  field: { flex: 1 },
  searching: { marginTop: theme.spacing(2) },
  searchingLabel: { display: "block", marginTop: 6, color: theme.palette.text.secondary },
  meter: { marginTop: theme.spacing(1.5) },
  meterLabel: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: 4 },
  upsell: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    // Theme-aware container (A5): light keeps the original blue-tint literals as
    // fallbacks; dark supplies a lighter translucent blue (tokens in themes.js)
    // so the upsell keeps a visible container on the dark surface.
    background: "var(--power-upsell-bg, rgba(30,79,214,0.06))",
    border: "1px solid var(--power-upsell-border, rgba(30,79,214,0.2))",
  },
  results: { marginTop: theme.spacing(2), display: "flex", flexDirection: "column", gap: 8 },
  countsLine: { fontSize: 12, color: theme.palette.text.secondary },
  tagRow: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 },
  tagChip: { fontSize: 11, height: 20 },
  hyChip: { fontSize: 11, height: 20, fontWeight: 700 },
  buildRow: { display: "flex", justifyContent: "flex-end", marginTop: 4 },
  result: { padding: theme.spacing(1.5), display: "flex", gap: 12, alignItems: "flex-start" },
  rank: { fontWeight: 700, color: theme.palette.text.secondary, minWidth: 22 },
  stem: { fontSize: 13, lineHeight: 1.5 },
  empty: { marginTop: theme.spacing(2) },
  emptyTitle: { fontWeight: 600 },
  emptyHint: { marginTop: 4, color: theme.palette.text.secondary, lineHeight: 1.5 },
  error: { color: theme.palette.error.main, marginTop: theme.spacing(1) },
}));

/*
 * Up to two short category tags per result — first discipline + first organ
 * system from the hydrated question's categories (design §3 step 2's
 * "category tags"). Capped so a many-category question can't turn a result
 * row into a chip wall.
 */
export function categoryTags(categories) {
  if (!categories) return [];
  // First entry only when it is a plain string — an object-shaped entry would
  // otherwise render an "[object Object]" chip. De-duped: a discipline and an
  // organ system sharing a label must not collide as React keys.
  const first = (v) => (Array.isArray(v) && typeof v[0] === "string" && v[0] ? v[0] : null);
  return [...new Set([first(categories.discipline), first(categories.organSystem)].filter(Boolean))];
}

function snippet(text, n = 240) {
  if (!text) return "(question text unavailable)";
  const s = text.replace(/\s+/g, " ").trim();
  // Truncate by code point so the cut can never split a surrogate pair
  // (same rule as powerSearchQuizName).
  const points = Array.from(s);
  return points.length > n ? `${points.slice(0, n).join("")}…` : s;
}

/**
 * Flag-gated semantic "Search by meaning" entry point. Calls the backend
 * POST /qlearn/quiz/power-search (404 while its flag is off — handled
 * gracefully), hydrates stems for display, and renders the per-user allowance
 * meter + upsell. Self-contained: it holds its own state and does not touch the
 * surrounding create-quiz flow.
 *
 * `onBuildQuiz(questionIds, query, examTypes)` closes the search→practice
 * loop: the page wires it into its normal createQuiz dispatch (see
 * onPowerSearchBuildQuiz), whose existing success/error effects then navigate
 * into the exam or surface the error. All three args are snapshots of the
 * SERVED search (ids, query text, exam scope), so a user editing the form
 * between search and click cannot drift the quiz away from its results.
 * Optional — without it the results stay read-only. `buildingQuiz` mirrors
 * the page's createQuiz loading state to guard double-taps.
 */
export default function PowerSearchPanel({ examTypes, onBuildQuiz, buildingQuiz = false }) {
  const classes = useStyles();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const trimmed = query.trim();
  const canSearch = trimmed.length >= MIN_QUERY && !loading;

  // `override` lets an example chip search its own text immediately, without
  // waiting for the setQuery state update to flush. The button passes its click
  // event here, so only accept a string override (an event is ignored → uses
  // the current query).
  const runSearch = async (override) => {
    const q = (typeof override === "string" ? override : query).trim();
    if (q.length < MIN_QUERY || loading) return;
    setLoading(true);
    setError("");
    try {
      const payload = { query: q };
      if (examTypes && examTypes.length) payload.examTypes = examTypes;
      const res = await api.powerSearch(payload);

      const status = res?.errStatus || res?.status;
      if (status === 404) {
        setData(null);
        setError("Power Search isn't available yet.");
        return;
      }
      if (res?.errStatus || res?.errMsg || !res) {
        setData(null);
        setError("Search failed. Please try again.");
        return;
      }

      const results = Array.isArray(res.results) ? res.results : [];
      const stems = {};
      const cats = {};
      if (results.length) {
        const ids = results.map((r) => r.questionId);
        const q = await api.getQuestionsByIds(ids);
        const list = Array.isArray(q) ? q : q?.questions || q?.data || [];
        (list || []).forEach((item) => {
          const id = item && (item.questionId || item.id || item._id);
          if (id) {
            stems[id] = item.stem;
            cats[id] = item.categories || null;
          }
        });
      }
      setData({
        // The query and exam scope these results answer (not the live input
        // box / form selection, which the user may have changed since) — the
        // build-quiz CTA must describe the served result set, not the current
        // form state, or the created quiz's metadata drifts from its contents.
        query: q,
        examTypes: examTypes && examTypes.length ? [...examTypes] : [],
        allowance: res.allowance || null,
        semanticApplied: !!res.semanticApplied,
        // True when we have results but couldn't load any previews, so the UI
        // can say so rather than silently showing placeholder cards.
        previewsFailed: results.length > 0 && Object.keys(stems).length === 0,
        // Server-computed high-yield count (null when the score lookup was
        // unavailable — the counts line then omits the High-Yield part).
        highYieldCount: typeof res.highYieldCount === "number" ? res.highYieldCount : null,
        results: results.map((r) => ({ ...r, stem: stems[r.questionId], categories: cats[r.questionId] })),
      });
    } catch (e) {
      setData(null);
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    // Enter runs the search; Shift+Enter inserts a newline (the field is
    // multiline for long scenarios). Cmd/Ctrl+Enter still works too. Skip while
    // an IME composition is active so composing text isn't submitted early.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      runSearch();
    }
  };

  // Surface how to run the search + why it's disabled, instead of a bare
  // character counter (the too-short state previously left the button silently
  // disabled). Fall back to the counter only as the query nears the limit.
  const helperText =
    trimmed.length > 0 && trimmed.length < MIN_QUERY
      ? `Type at least ${MIN_QUERY} characters to search`
      : query.length > MAX_QUERY * 0.85
      ? `${query.length}/${MAX_QUERY}`
      : "Enter to search · Shift+Enter for a new line";

  const allowance = data?.allowance;
  const blocked = allowance && allowance.allowed === false;

  // §6 gap-fill: the daily-limit Pro upsell (rendered below) is display-only
  // text with no click target, so it previously fired zero analytics. Emit an
  // impression event when it surfaces so the paywall-entry funnel is complete.
  // No markup/style change — tracking-only.
  const dailyProUpsellShown = !!(blocked && allowance.reason === REASON_DAILY && !allowance.pro);
  useEffect(() => {
    if (dailyProUpsellShown) {
      trackPaywallEntry('paywall_entry_shown', { source: 'power_search_upsell' });
    }
  }, [dailyProUpsellShown]);

  return (
    <Paper className={classes.root} elevation={0} variant="outlined" data-testid="power-search-panel">
      <div className={classes.header}>
        <Typography variant="h6">Search by meaning</Typography>
        <Chip size="small" color="primary" label="BETA" className={classes.beta} />
      </div>
      <Typography className={classes.sub} variant="body2">
        Describe what you want in plain language — e.g. “management of a bleeding duodenal ulcer”.
        Power Search ranks matching questions by meaning, not just keywords.
      </Typography>

      <div
        className={classes.examples}
        role="group"
        aria-label="Example searches"
        data-testid="power-search-examples"
      >
        <span className={classes.examplesLabel}>Try:</span>
        {EXAMPLE_QUERIES.map((ex) => (
          <Chip
            key={ex}
            size="small"
            variant="outlined"
            clickable
            // Don't let a chip swap the box text mid-search, or spend an
            // allowance once the daily limit is already hit.
            disabled={loading || blocked}
            label={ex}
            className={classes.exampleChip}
            onClick={() => {
              setQuery(ex);
              runSearch(ex);
            }}
            data-testid="power-search-example"
          />
        ))}
      </div>

      <div className={classes.row}>
        <TextField
          className={classes.field}
          variant="outlined"
          size="small"
          multiline
          placeholder="Describe the topic or scenario…"
          value={query}
          onChange={(e) => setQuery(e.target.value.slice(0, MAX_QUERY))}
          onKeyDown={onKeyDown}
          inputProps={{ maxLength: MAX_QUERY, "data-testid": "power-search-input" }}
          helperText={helperText}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={runSearch}
          disabled={!canSearch}
          data-testid="power-search-submit"
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </Button>
      </div>

      {loading && (
        <div className={classes.searching} data-testid="power-search-loading" aria-live="polite">
          <LinearProgress aria-label="Searching by meaning" />
          <Typography variant="caption" className={classes.searchingLabel}>
            Searching by meaning…
          </Typography>
        </div>
      )}

      {error && (
        <Typography className={classes.error} variant="body2" data-testid="power-search-error">
          {error}
        </Typography>
      )}

      {allowance && !allowance.pro && (
        <div className={classes.meter} data-testid="power-search-meter">
          <div className={classes.meterLabel}>
            {Math.max(allowance.dailyRemaining, 0)} of {allowance.dailyLimit} free searches left today
          </div>
          <LinearProgress
            variant="determinate"
            value={
              allowance.dailyLimit > 0
                ? Math.min(100, (allowance.dailyUsed / allowance.dailyLimit) * 100)
                : 0
            }
          />
        </div>
      )}

      {blocked && (
        <div className={classes.upsell} data-testid="power-search-blocked">
          {allowance.reason === REASON_BURST ? (
            <Typography variant="body2">
              You’re searching too fast — please try again in a minute.
            </Typography>
          ) : allowance.reason === REASON_DAILY && !allowance.pro ? (
            <Typography variant="body2" data-testid="power-search-upsell">
              <strong>You’ve used your free Power Searches for today.</strong> Upgrade to Pro for
              unlimited semantic search. Keyword search stays free and unlimited.
            </Typography>
          ) : (
            <Typography variant="body2">
              You’ve reached today’s Power Search limit. Please try again tomorrow.
            </Typography>
          )}
        </div>
      )}

      {data && !blocked && data.previewsFailed && (
        <Typography className={classes.error} variant="body2" data-testid="power-search-previews-failed">
          Found matches, but couldn’t load previews. Please try again.
        </Typography>
      )}

      {data && !blocked && data.results.length > 0 && !data.semanticApplied && (
        <Typography className={classes.sub} variant="caption" data-testid="power-search-fallback">
          Showing keyword matches (semantic ranking unavailable).
        </Typography>
      )}

      {data && !blocked && data.results.length > 0 && (
        <div className={classes.results} data-testid="power-search-results">
          <Typography className={classes.countsLine} data-testid="power-search-counts">
            {data.results.length} match{data.results.length === 1 ? "" : "es"}
            {typeof data.highYieldCount === "number" ? ` · ${data.highYieldCount} High-Yield` : ""}
          </Typography>
          {data.results.map((r) => (
            <Paper key={r.questionId} className={classes.result} elevation={0} variant="outlined">
              <span className={classes.rank}>{r.rank || "•"}</span>
              <Box flex={1}>
                <Typography className={classes.stem}>{snippet(r.stem)}</Typography>
                {(categoryTags(r.categories).length > 0 || r.highYield === true) && (
                  <div className={classes.tagRow} data-testid="power-search-tags">
                    {r.highYield === true && (
                      <Chip
                        size="small"
                        color="secondary"
                        label="High-Yield"
                        className={classes.hyChip}
                        data-testid="power-search-hy-badge"
                      />
                    )}
                    {categoryTags(r.categories).map((t) => (
                      <Chip key={t} size="small" variant="outlined" label={t} className={classes.tagChip} />
                    ))}
                  </div>
                )}
              </Box>
              {relevanceLabel(r.similarity) && (
                <Chip
                  size="small"
                  label={relevanceLabel(r.similarity)}
                  data-testid="power-search-relevance"
                />
              )}
            </Paper>
          ))}
          {onBuildQuiz && (
            <div className={classes.buildRow}>
              <Button
                variant="contained"
                color="primary"
                disabled={buildingQuiz}
                onClick={() => onBuildQuiz(data.results.map((r) => r.questionId), data.query, data.examTypes)}
                data-testid="power-search-build-quiz"
              >
                {buildingQuiz ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Practice these ${data.results.length} question${data.results.length === 1 ? "" : "s"}`
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {data && !blocked && !loading && data.results.length === 0 && (
        <div className={classes.empty} data-testid="power-search-empty" role="status">
          <Typography variant="body2" className={classes.emptyTitle}>
            No strong matches for that.
          </Typography>
          <Typography variant="body2" className={classes.emptyHint}>
            Try fewer or broader terms, or drop the specifics (ages, doses, labs) —
            Power Search matches on the clinical concept, not exact wording. The
            examples above are a good starting point.
          </Typography>
        </div>
      )}
    </Paper>
  );
}
